import React, { useMemo, useRef } from "react";
import Table from "react-bootstrap/Table";
import { Bar } from "react-chartjs-2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const utahData = "/img/headerlogo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdfIcon = "/img/pdf1.png";

const num = (v) => Number(v) || 0;
const fmt = (v) => num(v).toLocaleString("en-US");

const AeroTable9 = ({ data = [] }) => {
  const pdfRef = useRef(null);

  const cleaned = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.filter(
      (r) => String(r["Age Group Label"] || "").toLowerCase() !== "average"
    );
  }, [data]);

  const ageGroups = useMemo(
    () => cleaned.map((item) => item["Age Group Label"]),
    [cleaned]
  );

  const avgMale = useMemo(
    () => cleaned.map((item) => num(item["Average Count of Male Workers"])),
    [cleaned]
  );

  const computed = useMemo(() => {
    if (cleaned.length === 0) return null;

    // Find top age group
    let topIdx = 0;
    for (let i = 1; i < cleaned.length; i++) {
      if (avgMale[i] > avgMale[topIdx]) topIdx = i;
    }

    const overallAvg = avgMale.reduce((a, b) => a + b, 0) / cleaned.length;

    return {
      overallAvg,
      top: { label: ageGroups[topIdx], value: avgMale[topIdx] },
    };
  }, [cleaned, avgMale, ageGroups]);

  const chartData = useMemo(() => {
    return {
      labels: ageGroups,
      datasets: [
        {
          label: "Average Male Workers",
          data: avgMale,
          backgroundColor: "#0ea5e980",
          borderColor: "#0ea5e9",
          borderWidth: 1,
          borderRadius: 6,
        },
      ],
    };
  }, [ageGroups, avgMale]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: "Average Male Workers by Age Group (2018–2024)",
        font: { size: 16, weight: "bold" },
        padding: { top: 8, bottom: 8 },
      },
      legend: {
        position: "top",
        labels: { font: { size: 11 }, boxWidth: 12, padding: 10 },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `Avg Male Workers: ${fmt(ctx.parsed.y)}`,
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Age Group" },
        grid: { display: false },
        ticks: {
          font: { size: window.innerWidth < 768 ? 10 : 12 },
        },
      },
      y: {
        beginAtZero: true,
        title: { display: true, text: "Average Workers" },
        ticks: { callback: (v) => fmt(v) },
        grid: { display: true },
      },
    },
  };

  const downloadPDF = () => {
    const input = pdfRef.current;
    if (!input) return;

    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, pageWidth - 10, 35);
      };

      const addFooter = (pageNo) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY
        );
        pdf.text(
          `Page ${pageNo}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right"
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;
      addFooter(pageNumber);

      // Additional pages
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Branding footer block
      pdf.line(10, 260, pageWidth - 10, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("avg-male-workers-by-age-group.pdf");
    });
  };

  if (!data || data.length === 0) return <p>No data available</p>;

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white rounded-xl shadow-md"
        style={{
          background:
            "linear-gradient(135deg, rgba(239,246,255,1) 0%, rgba(255,255,255,1) 45%, rgba(253,242,248,1) 100%)",
        }}
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Age Group with Highest Male Workers (Averages, 2018–2024)
        </h2>

        {/* Summary Cards */}
        {computed && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 text-center">
            <div className="p-4 bg-blue-100 rounded-xl shadow">
              <p className="text-sm text-gray-600 font-medium">
                Overall Avg Male
              </p>
              <p className="text-xl font-bold text-blue-800">
                {fmt(computed.overallAvg)}
              </p>
            </div>

            <div className="p-4 bg-green-100 rounded-xl shadow">
              <p className="text-sm text-gray-600 font-medium">Top Age Group</p>
              <p className="text-xl font-bold text-green-800">
                {computed.top.label}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Avg: {fmt(computed.top.value)}
              </p>
            </div>
          </div>
        )}

        {/* Table + Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <Table
                striped
                bordered
                hover
                responsive
                className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden"
              >
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left text-sm">Age Group</th>
                    <th className="p-2 text-sm" style={{ textAlign: "right" }}>
                      Avg Male Workers
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {cleaned.map((item, index) => (
                    <tr
                      key={index}
                      className={`hover:bg-gray-50 ${
                        index % 2 ? "bg-gray-50/60" : "bg-white"
                      }`}
                    >
                      <td className="p-2 text-left text-sm">
                        {item["Age Group Label"]}
                      </td>
                      <td
                        className="p-2 text-sm"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {num(item["Average Count of Male Workers"]).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Tip: Scroll horizontally on mobile if needed.
            </p>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[520px] w-full h-96 p-4">
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Download PDF Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="PDF Icon" />
        </button>
      </div>
    </>
  );
};

export default AeroTable9;
